import { Environment } from "@axelar-network/axelarjs-sdk";
import { CosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import { formatUnits } from "@ethersproject/units";
import retry from "async-retry";
import BigNumberJS from "bignumber.js";
import { BigNumber, BigNumberish, ContractReceipt } from "ethers";
import { Interface } from "ethers/lib/utils";

import { getAllSupportedChains } from "../config";

export const getCosmosDefaultProvider = async (rpcUrl: string) => {
  return CosmWasmClient.connect(rpcUrl);
};

export const dateFormat = (date: Date) => {
  let day = date.getDate().toString();
  if (day.length === 1) {
    day = "0" + day;
  }
  let month = (1 + date.getMonth()).toString();
  if (month.length === 1) {
    month = "0" + month;
  }
  return `${day}/${month}/${date.getFullYear()}`;
};

export const formatDateToDDMMYY = (milliseconds: number) => {
  const date = new Date(milliseconds);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

export const abbreviatePrice = (value: number | string) => {
  const numberValue = parseFloat(value.toString());

  if (numberValue >= 1000000000) {
    return (numberValue / 1000000000).toFixed(1).replace(/\.0$/, "") + "T";
  } else if (numberValue >= 1000000) {
    return (numberValue / 1000000).toFixed(1).replace(/\.0$/, "") + "m";
  } else if (numberValue >= 1000) {
    return (numberValue / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return numberValue.toString();
  }
};

export const prettifyNumber = (number: number, precision = 2): string => {
  const integer = Math.trunc(number);
  if (number.toString().split(".").length === 2) {
    const rest = number.toFixed(precision).split(".")[1];
    return `${integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}.${
      rest
    }`;
  }
  return `${integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
};

export const addSeparatorsToNumber = (number: number): string => {
  const integer = Math.trunc(number);
  if (number.toString().split(".").length === 2) {
    return `${integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${
      number.toString().split(".")[1]
    }`;
  }
  return `${integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

export const roundToStep = (
  value: number,
  step: number,
  roundType: "ceil" | "floor" = "ceil",
) => {
  const factor = 1 / step;

  return Math[roundType](value * factor) / factor;
};

export const printAmount = (balance: number, precision = 2): string => {
  return addSeparatorsToNumber(
    +Number.parseFloat((balance / 1000000).toString()).toFixed(precision),
  );
};

export const secondsToDate = (seconds: BigNumber | number) => {
  return new Date(
    parseInt((parseInt(seconds.toString(), 10) * 1000).toString()),
  );
};

export const trimTxHash = (hash: string, beginAndEndCharacters: number = 5) => {
  if (!hash) {
    return "";
  }
  return `${hash?.substring(0, beginAndEndCharacters)}...${hash.substring(
    hash.length - beginAndEndCharacters,
    hash.length,
  )}`;
};

export const formatUnitsSignificant = ({
  value,
  significantNumbers = 3,
  decimals = 18,
}: {
  value: BigNumberish;
  significantNumbers?: number;
  decimals?: number;
}): string => {
  if (typeof value === "string") {
    if (value.indexOf(".") > -1) {
      const [integerPart, decimalPart] = value.split(".");
      decimals = decimalPart.length;
      value = integerPart + decimalPart;
    }
  }

  const formatted = formatUnits(value, decimals);
  // match from the beginning up to the first number > 0 after the dot
  const regexResult = formatted.match(/[0-9]+\.0*([1-9])/);

  // the match will not be found if there is no number > 0 after the dot, 123.0
  if (!regexResult) {
    return formatted;
  }

  // generate number of zeros = significantNumbers
  const safetyZeros = [...Array(significantNumbers)].map(() => "0").join("");
  // add zeros at the end to prevent index out of range when slicing
  const formattedWithSafetyZeros = `${formatted}${safetyZeros}`;
  const end = regexResult[0].length;
  let cutNumber = formattedWithSafetyZeros.slice(
    0,
    end + significantNumbers - 1,
  );

  // remove remaining zeros: 0.0123000 -> 0.0123
  let noRemainingZerosNumber = cutNumber;

  for (let i = cutNumber.length - 1; i > 0; i--) {
    if (cutNumber[i] !== "0") {
      noRemainingZerosNumber = cutNumber.substr(0, i + 1);
      break;
    }
  }

  // prevents producing 0. for 0.123 and significantNumbers = 0
  return noRemainingZerosNumber.endsWith(".")
    ? `${noRemainingZerosNumber}0`
    : noRemainingZerosNumber;
};

export const parseError = (err: any) => {
  if (err?.data?.message) {
    if (err?.data?.message.includes("insufficient funds")) {
      return "Insufficient funds";
    }
  }
  return `${err?.reason || err?.message || "unknown error"}`;
};

export const shortAddress = (address: string): string => {
  return `${address?.substring(0, 5)}...${address?.substring(
    address.length - 5,
    address.length,
  )}`;
};

export const shortAddressMobile = (address: string): string => {
  return `${address?.substring(0, 5)}...`;
};

export const isEVMAddressValid = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

export const isEmailAddressValid = (emailAddress: string): boolean => {
  return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(
    emailAddress,
  );
};

export const isCosmosAddressValid = (
  address: string,
  chainPrefix?: string,
): boolean => {
  let regex = new RegExp(`^.+?1[a-z0-9]{38}$`, "is");
  if (chainPrefix) {
    regex = new RegExp(`^${chainPrefix}1[a-z0-9]{38}$`, "is");
  }
  return regex.test(address);
};

export const validateEVMAddress = (evmAddressToCheck: string) => {
  if (evmAddressToCheck !== "" && !isEVMAddressValid(evmAddressToCheck)) {
    return "Provided EVM address is invalid!";
  }
  return "";
};

export const validateCosmosAddress = (
  cosmosAddressToCheck: string,
  chainPrefix: string,
) => {
  if (
    cosmosAddressToCheck !== "" &&
    !isCosmosAddressValid(cosmosAddressToCheck, chainPrefix)
  ) {
    return `Provided ${chainPrefix} address is invalid!`;
  }
  return "";
};

export const delay = async (duration) =>
  new Promise((resolve) => setTimeout(resolve, duration));

export const asyncSetInterval = (func: () => Promise<void>, delay = 3000) => {
  let lock = false;
  const initial = async () => {
    lock = true;
    await func();
    lock = false;
  };
  initial();
  const intervalId = setInterval(async () => {
    if (!lock) {
      lock = true;
      await func();
      lock = false;
    }
  }, delay);
  return intervalId;
};

export interface IWrapWithRetries {
  queries: ((bail?: any) => Promise<any>)[];
  expectedErrorReasons?: string[];
  delayInMs?: number;
  numberOfRetries?: number;
}

export const retryFunctionWrapperOmitNoOwnerError = (fn) => {
  return async (bail) => {
    try {
      return await fn();
    } catch (error) {
      if (
        (error as Error).message.includes(
          "ERC721: owner query for nonexistent token",
        ) ||
        (error as Error).message.includes("ERC721: invalid token ID")
      ) {
        bail();
        return;
      }
    }
  };
};
export const wrapWithRetries = async ({
  queries,
  delayInMs = 3000,
  numberOfRetries = 3,
}: IWrapWithRetries) => {
  let currentQueries = queries.map((query) =>
    retry(query, {
      minTimeout: delayInMs,
      retries: numberOfRetries,
    }),
  );

  return (await Promise.allSettled(currentQueries))
    .map((response, index) => ({ ...response, queryIndex: index }))
    .filter(
      (response) =>
        response.status === "fulfilled" && response.value !== undefined,
    );
};

export const getParsedEventLogs = (
  txReceipt: ContractReceipt,
  contractInterface: Interface,
) => {
  return txReceipt.logs
    .map((rawLog) => {
      try {
        let log = contractInterface.parseLog(rawLog);
        return log;
      } catch (error) {
        return null;
      }
    })
    .filter((log) => log);
};

const allSupportedChains = getAllSupportedChains();

export const isTestnetChain = (chainId: string): boolean => {
  return allSupportedChains[chainId]?.chainType === Environment.TESTNET;
};

export const weiToHumanReadable = ({
  amount,
  decimals,
  precisionFractionalPlaces,
}: {
  amount: string;
  decimals: number;
  precisionFractionalPlaces: number;
}): string => {
  const decimalsFactor = Math.pow(10, decimals);

  BigNumberJS.config({ DECIMAL_PLACES: precisionFractionalPlaces });

  return new BigNumberJS(amount).div(decimalsFactor).toFixed();
};

export const humanReadableToWei = ({
  amount,
  decimals,
}: {
  amount: string;
  decimals: number;
}): string => {
  const decimalsFactor = Math.pow(10, decimals);
  const parsedAmount = new BigNumberJS(amount);
  const result = parsedAmount.multipliedBy(decimalsFactor).toFixed();

  // BigNumberJS returns "NaN" string
  if (result === "NaN") {
    throw new Error("humanReadableToWei > result is NaN");
  }

  return result;
};

const MS_IN_S = 1000;
const MS_IN_MIN = MS_IN_S * 60;
const MS_IN_H = MS_IN_MIN * 60;

export const getEstimatedTimeLeft = ({
  endTimestampMs,
  currentTimestampMs,
}: {
  endTimestampMs: number;
  currentTimestampMs: number;
}) => {
  let timeLeftMs = Math.max(0, endTimestampMs - currentTimestampMs);

  const hours = Math.floor(timeLeftMs / MS_IN_H);
  timeLeftMs -= hours * MS_IN_H;
  const minutes = Math.floor(timeLeftMs / MS_IN_MIN);
  timeLeftMs -= minutes * MS_IN_MIN;
  const seconds = Math.floor(timeLeftMs / MS_IN_S);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes}min`);
  parts.push(`${seconds}s`);

  return parts.join(" ");
};

export const validateNumberInput = (value: string) => {
  if (value.startsWith(".")) {
    value = "0.";
  } else {
    value = value.replace(/-/g, "");
  }

  if (/^0+(?=\d)/.test(value)) {
    value = value.replace(/^0+/, "0");
  }

  if (/^(\d*\.?\d*)$/.test(value)) {
    return value;
  }
  return "";
};
// this function returns a new array with every array.length/(numElements-1)th element,
// the rest of the elements are turned to ""
// example: arr:[1,2,3,4,5,6,7,8,9,10] numElements:5
// output: ['1', '', '3', '', '5', '', '7', '', '', '10']
export const selectNthElements = (
  arr: string[] | number[],
  numElements: number,
) => {
  const length = arr.length;
  if (length <= numElements * 2) {
    return arr;
  }
  if (numElements <= 1) {
    return length > 0 ? [arr[0]] : [];
  } else {
    const indices = Array.from({ length: numElements }, (_, i) =>
      Math.floor(((length - 1) * i) / (numElements - 1)),
    );
    const result = Array(length).fill("");
    indices.forEach((index) => {
      result[index] = arr[index];
    });
    return result;
  }
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const abbreviateNumber = (input: string | number): string => {
  const number =
    typeof input === "string" ? parseInt(input.replace(/\D/g, "")) : input;

  if (number >= 1e9) {
    return (number / 1e9).toFixed(2) + " B";
  } else if (number >= 1e6) {
    return (number / 1e6).toFixed(2) + " M";
  } else if (number >= 1e3) {
    return (number / 1e3).toFixed(2) + " K";
  } else {
    return number.toString();
  }
};

export const tokenAmountDividedByDecimals = ({
  amount,
  decimals,
}: {
  amount: string;
  decimals: number;
}) => {
  return new BigNumberJS(amount).div(Math.pow(10, decimals)).toFixed(2);
};
